import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Upload, Plus, X, Check, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Turnstile } from '@marsidev/react-turnstile';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TURNSTILE_SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY;

const SocialFacesApplicationPage = () => {
    const isGermany = window.location.pathname.includes('-de');
    const countryFlag = isGermany ? '🇩🇪' : '🇦🇹';
    const country = isGermany ? 'DE' : 'AT';
    
    const [currentSlide, setCurrentSlide] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [applicationId, setApplicationId] = useState(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [countdown, setCountdown] = useState(6);
    const [whatsappLink, setWhatsappLink] = useState('https://chat.whatsapp.com/HLQY1yuMozWIbSz2iiyhUE');
    const [consentPdfUrl, setConsentPdfUrl] = useState(null);
    const [showConsentPopup, setShowConsentPopup] = useState(false);
    const [turnstileToken, setTurnstileToken] = useState(null);
    // Vier getrennte Einwilligungen (DSGVO: granular, nichts vorangekreuzt) + Erziehungsberechtigte unter 18
    const [consentDocs, setConsentDocs] = useState([]);
    const [consents, setConsents] = useState({});
    const [guardian, setGuardian] = useState({ name: '', contact: '', doc_url: '', uploading: false });
    useEffect(() => {
        axios.get(`${API_URL}/api/sf/consents/documents`).then(r => setConsentDocs(r.data || [])).catch(() => {});
    }, []);
    const computeAge = (d) => { if (!d) return null; const t = new Date(); let a = t.getFullYear() - d.getFullYear(); const m = t.getMonth() - d.getMonth(); if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1; return a; };
    const allConsentsGiven = consentDocs.length > 0 && consentDocs.every(d => consents[d.key]);
    const uploadGuardianDoc = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setGuardian(g => ({ ...g, uploading: true }));
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await axios.post(`${API_URL}/api/social-faces/upload-guardian-document`, fd);
            setGuardian(g => ({ ...g, doc_url: r.data.url, uploading: false }));
            toast.success('Erklärung hochgeladen');
        } catch (err) {
            setGuardian(g => ({ ...g, uploading: false }));
            toast.error(err.response?.data?.detail || 'Upload fehlgeschlagen');
        }
    };
    
    const fileInputRef = useRef(null);
    
    // Form data
    const [formData, setFormData] = useState({
        gender: '',
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        birthday: null,
        city: '',
        postal_code: '',
        nationality: '',
        german_level: '',
        height: '',
        hair_color: '',
        glasses: false,
        piercings: false,
        tattoos: false,
        diet: '',
        allergies: false,
        allergies_details: '',
        other_info: false,
        other_info_details: '',
        profile_image: null,
        profile_image_preview: null,
        social_links: [''],
        consent_accepted: false,
        country: country
    });

    // Load public settings
    useEffect(() => {
        const loadSettings = async () => {
            try {
                const res = await axios.get(`${API_URL}/api/social-faces/settings/public`);
                if (res.data.whatsapp_link) setWhatsappLink(res.data.whatsapp_link);
                if (res.data.consent_pdf_url) setConsentPdfUrl(res.data.consent_pdf_url);
            } catch (e) {
                console.error('Failed to load settings:', e);
            }
        };
        loadSettings();
    }, []);

    // Countdown and redirect after success
    useEffect(() => {
        if (showSuccess && countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        } else if (showSuccess && countdown === 0) {
            // Use location.href instead of window.open to avoid popup blockers
            window.location.href = whatsappLink;
        }
    }, [showSuccess, countdown, whatsappLink]);

    const totalSlides = 13;

    const updateField = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const addSocialLink = () => {
        setFormData(prev => ({ ...prev, social_links: [...prev.social_links, ''] }));
    };

    const updateSocialLink = (index, value) => {
        const newLinks = [...formData.social_links];
        newLinks[index] = value;
        setFormData(prev => ({ ...prev, social_links: newLinks }));
    };

    const removeSocialLink = (index) => {
        if (formData.social_links.length > 1) {
            const newLinks = formData.social_links.filter((_, i) => i !== index);
            setFormData(prev => ({ ...prev, social_links: newLinks }));
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Check file size (35MB)
        if (file.size > 35 * 1024 * 1024) {
            toast.error('Datei zu groß. Maximum 35MB.');
            return;
        }

        // Preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setFormData(prev => ({
                ...prev,
                profile_image: file,
                profile_image_preview: reader.result
            }));
        };
        reader.readAsDataURL(file);
    };

    const canProceed = () => {
        switch (currentSlide) {
            case 0: return !!formData.gender;
            case 1: return formData.first_name && formData.last_name && formData.email && formData.phone;
            case 2: return !!formData.birthday;
            case 3: return formData.city && formData.postal_code;
            case 4: return !!formData.nationality;
            case 5: return !!formData.german_level;
            case 6: return formData.height && formData.hair_color;
            case 7: return !!formData.diet;
            case 8: return formData.allergies === false || (formData.allergies && formData.allergies_details);
            case 9: return formData.other_info === false || (formData.other_info && formData.other_info_details);
            case 10: return !!formData.profile_image;
            case 11: return formData.social_links.some(link => link.trim());
            case 12: {
                const minor = computeAge(formData.birthday) !== null && computeAge(formData.birthday) < 18;
                const guardianOk = !minor || (guardian.name.trim() && guardian.contact.trim() && guardian.doc_url);
                return allConsentsGiven && guardianOk && (!TURNSTILE_SITE_KEY || turnstileToken);
            }
            default: return true;
        }
    };

    const handleSubmit = async () => {
        if (!allConsentsGiven) {
            toast.error('Bitte stimme allen vier Erklärungen einzeln zu');
            return;
        }
        const isMinor = computeAge(formData.birthday) !== null && computeAge(formData.birthday) < 18;
        if (isMinor && !(guardian.name.trim() && guardian.contact.trim() && guardian.doc_url)) {
            toast.error('Unter 18: Bitte Erziehungsberechtigte angeben und die unterschriebene Erklärung hochladen');
            return;
        }

        if (TURNSTILE_SITE_KEY && !turnstileToken) {
            toast.error('Bitte bestätige, dass du kein Roboter bist');
            return;
        }

        setIsSubmitting(true);

        try {
            // First, submit the application
            const submitData = {
                ...formData,
                birthday: formData.birthday ? format(formData.birthday, 'yyyy-MM-dd') : null,
                social_links: formData.social_links.filter(link => link.trim()),
                turnstile_token: turnstileToken,
                consent_accepted: allConsentsGiven,
                consents: consentDocs.filter(d => consents[d.key]).map(d => d.key),
                guardian_name: isMinor ? guardian.name.trim() : null,
                guardian_contact: isMinor ? guardian.contact.trim() : null,
                guardian_doc_url: isMinor ? guardian.doc_url : null
            };
            delete submitData.profile_image;
            delete submitData.profile_image_preview;

            const res = await axios.post(`${API_URL}/api/social-faces/apply`, submitData);
            
            if (res.data.success) {
                const appId = res.data.application_id;
                setApplicationId(appId);
                setWhatsappLink(res.data.whatsapp_link || whatsappLink);

                // Upload image
                if (formData.profile_image) {
                    setIsUploading(true);
                    const imageFormData = new FormData();
                    imageFormData.append('file', formData.profile_image);
                    
                    try {
                        await axios.post(`${API_URL}/api/social-faces/upload-image/${appId}`, imageFormData);
                    } catch (imgErr) {
                        console.error('Image upload failed:', imgErr);
                    }
                    setIsUploading(false);
                }

                setShowSuccess(true);
            }
        } catch (err) {
            console.error('Submit error:', err);
            toast.error(err.response?.data?.detail || 'Fehler beim Senden der Bewerbung');
        } finally {
            setIsSubmitting(false);
        }
    };

    const slideVariants = {
        enter: (direction) => ({ x: direction > 0 ? 300 : -300, opacity: 0 }),
        center: { x: 0, opacity: 1 },
        exit: (direction) => ({ x: direction < 0 ? 300 : -300, opacity: 0 })
    };

    const [direction, setDirection] = useState(0);

    const goNext = () => {
        if (canProceed() && currentSlide < totalSlides - 1) {
            setDirection(1);
            setCurrentSlide(currentSlide + 1);
        }
    };

    const goPrev = () => {
        if (currentSlide > 0) {
            setDirection(-1);
            setCurrentSlide(currentSlide - 1);
        }
    };

    // Success Screen
    if (showSuccess) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
                <div className="flex-1 flex items-center justify-center p-4">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        className="bg-white/10 backdrop-blur-xl rounded-3xl p-8 max-w-md w-full text-center border border-white/20"
                    >
                        {/* Logo */}
                        <img 
                            src="/tgosm-logo.png" 
                            alt="TGOSM" 
                            className="w-16 h-16 mx-auto mb-4 object-contain"
                        />
                        
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ delay: 0.2, type: 'spring' }}
                            className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6"
                        >
                            <Check className="w-10 h-10 text-white" />
                        </motion.div>
                        
                        <h1 className="text-2xl font-bold text-white mb-4">
                            Vielen Dank für deine Bewerbung! {countryFlag}
                        </h1>
                        
                        <p className="text-white/70 mb-6">
                            Du wirst in <span className="text-2xl font-bold text-primary">{countdown}</span> Sekunden 
                            zu unserer WhatsApp Shooting-Gruppe weitergeleitet.
                        </p>
                        
                        <div className="text-sm text-white/50 mb-4">
                            Hat die Weiterleitung nicht funktioniert?
                        </div>
                        
                        <a 
                            href={whatsappLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-2 text-green-400 hover:text-green-300"
                        >
                            <ExternalLink className="w-4 h-4" />
                            Klicke hier zur Gruppe
                        </a>
                    </motion.div>
                </div>
                
                {/* Footer */}
                <footer className="py-6 text-center text-white/40 text-sm">
                    <p>© {new Date().getFullYear()} The Guardians of Social Media GmbH. Alle Rechte vorbehalten.</p>
                </footer>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex flex-col">
            <div className="flex-1 flex items-center justify-center p-4 py-8">
                <div className="w-full max-w-lg">
                    {/* Header with Logo */}
                    <div className="text-center mb-8">
                        <img 
                            src="/tgosm-logo.png" 
                            alt="TGOSM" 
                            className="w-20 h-20 mx-auto mb-4 object-contain"
                        />
                        <h1 className="text-3xl font-bold text-white mb-2">
                            Social Face Bewerbung {countryFlag}
                        </h1>
                        <p className="text-white/60">The Guardians of Social Media</p>
                    </div>

                {/* Progress */}
                <div className="flex gap-1 mb-6">
                    {Array.from({ length: totalSlides }).map((_, i) => (
                        <div
                            key={i}
                            className={`h-1 flex-1 rounded-full transition-all ${
                                i <= currentSlide ? 'bg-primary' : 'bg-white/20'
                            }`}
                        />
                    ))}
                </div>
                <div className="text-center text-white/50 text-sm mb-4">
                    {currentSlide + 1} von {totalSlides}
                </div>

                {/* Form Card */}
                <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 border border-white/20 min-h-[400px] relative overflow-hidden">
                    <AnimatePresence mode="wait" custom={direction}>
                        <motion.div
                            key={currentSlide}
                            custom={direction}
                            variants={slideVariants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ duration: 0.3 }}
                            className="space-y-4"
                        >
                            {/* Slide 0: Gender */}
                            {currentSlide === 0 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Geschlecht *</Label>
                                    <div className="space-y-3">
                                        {[
                                            { value: 'männlich', label: 'Männlich' },
                                            { value: 'weiblich', label: 'Weiblich' },
                                            { value: 'divers', label: 'Divers' }
                                        ].map(opt => (
                                            <button
                                                key={opt.value}
                                                onClick={() => updateField('gender', opt.value)}
                                                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                    formData.gender === opt.value
                                                        ? 'border-primary bg-primary/20 text-white'
                                                        : 'border-white/20 text-white/70 hover:border-white/40'
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Slide 1: Name & Contact */}
                            {currentSlide === 1 && (
                                <div className="space-y-4">
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-white">Vorname *</Label>
                                            <Input
                                                value={formData.first_name}
                                                onChange={(e) => updateField('first_name', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="Max"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-white">Nachname *</Label>
                                            <Input
                                                value={formData.last_name}
                                                onChange={(e) => updateField('last_name', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="Mustermann"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <Label className="text-white">E-Mail *</Label>
                                        <Input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => updateField('email', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white mt-1"
                                            placeholder="max@beispiel.at"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-white">Telefonnummer *</Label>
                                        <Input
                                            type="tel"
                                            value={formData.phone}
                                            onChange={(e) => updateField('phone', e.target.value)}
                                            className="bg-white/10 border-white/20 text-white mt-1"
                                            placeholder="+43 660 1234567"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Slide 2: Birthday */}
                            {currentSlide === 2 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Geburtstag *</Label>
                                    <div className="grid grid-cols-3 gap-3">
                                        {/* Day */}
                                        <Select 
                                            value={formData.birthday ? String(formData.birthday.getDate()) : ''}
                                            onValueChange={(day) => {
                                                const current = formData.birthday || new Date(2000, 0, 1);
                                                const newDate = new Date(current.getFullYear(), current.getMonth(), parseInt(day));
                                                updateField('birthday', newDate);
                                            }}
                                        >
                                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                                <SelectValue placeholder="Tag" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[200px]">
                                                {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                                    <SelectItem key={day} value={String(day)}>{day}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        
                                        {/* Month */}
                                        <Select 
                                            value={formData.birthday ? String(formData.birthday.getMonth()) : ''}
                                            onValueChange={(month) => {
                                                const current = formData.birthday || new Date(2000, 0, 1);
                                                const newDate = new Date(current.getFullYear(), parseInt(month), current.getDate());
                                                updateField('birthday', newDate);
                                            }}
                                        >
                                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                                <SelectValue placeholder="Monat" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[200px]">
                                                {['Januar', 'Februar', 'März', 'April', 'Mai', 'Juni', 'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'].map((month, i) => (
                                                    <SelectItem key={i} value={String(i)}>{month}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                        
                                        {/* Year */}
                                        <Select 
                                            value={formData.birthday ? String(formData.birthday.getFullYear()) : ''}
                                            onValueChange={(year) => {
                                                const current = formData.birthday || new Date(2000, 0, 1);
                                                const newDate = new Date(parseInt(year), current.getMonth(), current.getDate());
                                                updateField('birthday', newDate);
                                            }}
                                        >
                                            <SelectTrigger className="bg-white/10 border-white/20 text-white">
                                                <SelectValue placeholder="Jahr" />
                                            </SelectTrigger>
                                            <SelectContent className="max-h-[200px]">
                                                {Array.from({ length: 61 }, (_, i) => 2010 - i).map(year => (
                                                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    {formData.birthday && (
                                        <p className="text-white/60 text-sm text-center">
                                            {format(formData.birthday, 'dd. MMMM yyyy', { locale: de })}
                                        </p>
                                    )}
                                </div>
                            )}

                            {/* Slide 3: Location */}
                            {currentSlide === 3 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Wohnort *</Label>
                                    <div className="grid grid-cols-3 gap-4">
                                        <div className="col-span-2">
                                            <Label className="text-white/70 text-sm">Ort</Label>
                                            <Input
                                                value={formData.city}
                                                onChange={(e) => updateField('city', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="Wien"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-white/70 text-sm">PLZ</Label>
                                            <Input
                                                value={formData.postal_code}
                                                onChange={(e) => updateField('postal_code', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="1010"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Slide 4: Nationality */}
                            {currentSlide === 4 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Was ist deine Nationalität? *</Label>
                                    <Input
                                        value={formData.nationality}
                                        onChange={(e) => updateField('nationality', e.target.value)}
                                        className="bg-white/10 border-white/20 text-white"
                                        placeholder="z.B. Österreichisch"
                                    />
                                </div>
                            )}

                            {/* Slide 5: German Level */}
                            {currentSlide === 5 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Wie schätzt du deine Deutschkenntnisse ein? *</Label>
                                    <div className="space-y-2 max-h-[300px] overflow-y-auto">
                                        {[
                                            { value: 'muttersprache', label: 'Muttersprache', desc: 'Ich spreche fließend und sicher.' },
                                            { value: 'fortgeschritten', label: 'Fortgeschritten', desc: 'Ich kann mich gut ausdrücken, aber manchmal fehlt mir noch etwas Sicherheit.' },
                                            { value: 'mittelstufe', label: 'Mittelstufe', desc: 'Ich verstehe und spreche gut, mache aber noch einige Fehler.' },
                                            { value: 'anfaenger', label: 'Anfänger', desc: 'Ich kenne die Grundlagen und lerne noch.' },
                                            { value: 'keine', label: 'Keine Kenntnisse', desc: 'Ich spreche oder verstehe kaum Deutsch.' }
                                        ].map(opt => (
                                            <button
                                                key={opt.value}
                                                onClick={() => updateField('german_level', opt.value)}
                                                className={`w-full p-3 rounded-xl border-2 transition-all text-left ${
                                                    formData.german_level === opt.value
                                                        ? 'border-primary bg-primary/20'
                                                        : 'border-white/20 hover:border-white/40'
                                                }`}
                                            >
                                                <div className="text-white font-medium">{opt.label}</div>
                                                <div className="text-white/50 text-sm">{opt.desc}</div>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Slide 6: Physical */}
                            {currentSlide === 6 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Körperliche Merkmale</Label>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <Label className="text-white/70 text-sm">Größe (cm) *</Label>
                                            <Input
                                                value={formData.height}
                                                onChange={(e) => updateField('height', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="175"
                                            />
                                        </div>
                                        <div>
                                            <Label className="text-white/70 text-sm">Haarfarbe *</Label>
                                            <Input
                                                value={formData.hair_color}
                                                onChange={(e) => updateField('hair_color', e.target.value)}
                                                className="bg-white/10 border-white/20 text-white mt-1"
                                                placeholder="Braun"
                                            />
                                        </div>
                                    </div>
                                    <div className="space-y-3 pt-2">
                                        <label className="flex items-center gap-3 p-3 rounded-xl border border-white/20 cursor-pointer hover:bg-white/5">
                                            <Checkbox
                                                checked={formData.glasses}
                                                onCheckedChange={(c) => updateField('glasses', c)}
                                            />
                                            <span className="text-white">Trägst du eine Brille?</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-3 rounded-xl border border-white/20 cursor-pointer hover:bg-white/5">
                                            <Checkbox
                                                checked={formData.piercings}
                                                onCheckedChange={(c) => updateField('piercings', c)}
                                            />
                                            <span className="text-white">Hast du Piercings?</span>
                                        </label>
                                        <label className="flex items-center gap-3 p-3 rounded-xl border border-white/20 cursor-pointer hover:bg-white/5">
                                            <Checkbox
                                                checked={formData.tattoos}
                                                onCheckedChange={(c) => updateField('tattoos', c)}
                                            />
                                            <span className="text-white">Hast du Tattoos?</span>
                                        </label>
                                    </div>
                                </div>
                            )}

                            {/* Slide 7: Diet */}
                            {currentSlide === 7 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Hast du einen bestimmten Ernährungsstil? *</Label>
                                    <div className="space-y-2">
                                        {[
                                            { value: 'alles', label: 'Nein, ich esse alles.' },
                                            { value: 'vegetarisch', label: 'Ich ernähre mich vegetarisch.' },
                                            { value: 'vegan', label: 'Ich ernähre mich vegan.' },
                                            { value: 'kein_schwein', label: 'Ich esse kein Schweinefleisch.' }
                                        ].map(opt => (
                                            <button
                                                key={opt.value}
                                                onClick={() => updateField('diet', opt.value)}
                                                className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                    formData.diet === opt.value
                                                        ? 'border-primary bg-primary/20 text-white'
                                                        : 'border-white/20 text-white/70 hover:border-white/40'
                                                }`}
                                            >
                                                {opt.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Slide 8: Allergies */}
                            {currentSlide === 8 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Hast du irgendwelche Allergien? *</Label>
                                    <div className="space-y-3">
                                        <button
                                            onClick={() => { updateField('allergies', false); updateField('allergies_details', ''); }}
                                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                formData.allergies === false
                                                    ? 'border-primary bg-primary/20 text-white'
                                                    : 'border-white/20 text-white/70 hover:border-white/40'
                                            }`}
                                        >
                                            Nein
                                        </button>
                                        <button
                                            onClick={() => updateField('allergies', true)}
                                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                formData.allergies === true
                                                    ? 'border-primary bg-primary/20 text-white'
                                                    : 'border-white/20 text-white/70 hover:border-white/40'
                                            }`}
                                        >
                                            Ja
                                        </button>
                                        {formData.allergies && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                            >
                                                <Input
                                                    value={formData.allergies_details}
                                                    onChange={(e) => updateField('allergies_details', e.target.value)}
                                                    className="bg-white/10 border-white/20 text-white"
                                                    placeholder="Welche Allergien?"
                                                />
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Slide 9: Other Info */}
                            {currentSlide === 9 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Müssen wir noch etwas über dich wissen?</Label>
                                    <div className="space-y-3">
                                        <button
                                            onClick={() => { updateField('other_info', false); updateField('other_info_details', ''); }}
                                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                formData.other_info === false
                                                    ? 'border-primary bg-primary/20 text-white'
                                                    : 'border-white/20 text-white/70 hover:border-white/40'
                                            }`}
                                        >
                                            Nein
                                        </button>
                                        <button
                                            onClick={() => updateField('other_info', true)}
                                            className={`w-full p-4 rounded-xl border-2 transition-all text-left ${
                                                formData.other_info === true
                                                    ? 'border-primary bg-primary/20 text-white'
                                                    : 'border-white/20 text-white/70 hover:border-white/40'
                                            }`}
                                        >
                                            Ja
                                        </button>
                                        {formData.other_info && (
                                            <motion.div
                                                initial={{ opacity: 0, height: 0 }}
                                                animate={{ opacity: 1, height: 'auto' }}
                                            >
                                                <textarea
                                                    value={formData.other_info_details}
                                                    onChange={(e) => updateField('other_info_details', e.target.value)}
                                                    className="w-full p-3 rounded-xl bg-white/10 border border-white/20 text-white resize-none"
                                                    rows={3}
                                                    placeholder="Was sollten wir wissen?"
                                                />
                                            </motion.div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Slide 10: Image Upload */}
                            {currentSlide === 10 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">Bitte lade ein Bild von dir hoch *</Label>
                                    <p className="text-white/50 text-sm">Maximale Dateigröße: 35MB</p>
                                    
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept="image/*"
                                        onChange={handleImageUpload}
                                        className="hidden"
                                    />
                                    
                                    {formData.profile_image_preview ? (
                                        <div className="relative">
                                            <img
                                                src={formData.profile_image_preview}
                                                alt="Vorschau"
                                                className="w-full h-64 object-cover rounded-xl"
                                            />
                                            <button
                                                onClick={() => { updateField('profile_image', null); updateField('profile_image_preview', null); }}
                                                className="absolute top-2 right-2 p-2 bg-red-500 rounded-full text-white"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full h-64 border-2 border-dashed border-white/30 rounded-xl flex flex-col items-center justify-center gap-4 hover:border-primary/50 hover:bg-white/5 transition-all"
                                        >
                                            <Upload className="w-12 h-12 text-white/50" />
                                            <span className="text-white/50">Klicken zum Hochladen</span>
                                        </button>
                                    )}
                                </div>
                            )}

                            {/* Slide 11: Social Links */}
                            {currentSlide === 11 && (
                                <div className="space-y-4">
                                    <Label className="text-white text-lg">
                                        Wir brauchen einen Link zu deinem Facebook oder Instagram-Profil *
                                    </Label>
                                    <div className="space-y-3">
                                        {formData.social_links.map((link, index) => (
                                            <div key={index} className="flex gap-2">
                                                <Input
                                                    value={link}
                                                    onChange={(e) => updateSocialLink(index, e.target.value)}
                                                    className="bg-white/10 border-white/20 text-white flex-1"
                                                    placeholder="https://instagram.com/deinprofil"
                                                />
                                                {formData.social_links.length > 1 && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => removeSocialLink(index)}
                                                        className="text-red-400 hover:text-red-300"
                                                    >
                                                        <X className="w-4 h-4" />
                                                    </Button>
                                                )}
                                            </div>
                                        ))}
                                        <Button
                                            variant="outline"
                                            onClick={addSocialLink}
                                            className="w-full border-dashed border-white/30 text-white/70 hover:bg-white/5"
                                        >
                                            <Plus className="w-4 h-4 mr-2" /> Weiteren Link hinzufügen
                                        </Button>
                                    </div>
                                </div>
                            )}

                            {/* Slide 12: Summary & Consent */}
                            {currentSlide === 12 && (
                                <div className="space-y-4 max-h-[350px] overflow-y-auto pr-2">
                                    <Label className="text-white text-lg">Zusammenfassung</Label>
                                    
                                    <div className="space-y-2 text-sm">
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <span className="text-white/50">Name:</span>
                                            <span className="text-white ml-2">{formData.first_name} {formData.last_name}</span>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <span className="text-white/50">E-Mail:</span>
                                            <span className="text-white ml-2">{formData.email}</span>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <span className="text-white/50">Telefon:</span>
                                            <span className="text-white ml-2">{formData.phone}</span>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <span className="text-white/50">Geburtstag:</span>
                                            <span className="text-white ml-2">
                                                {formData.birthday ? format(formData.birthday, 'dd.MM.yyyy') : '-'}
                                            </span>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <span className="text-white/50">Ort:</span>
                                            <span className="text-white ml-2">{formData.postal_code} {formData.city}</span>
                                        </div>
                                        <div className="p-3 bg-white/5 rounded-lg">
                                            <span className="text-white/50">Nationalität:</span>
                                            <span className="text-white ml-2">{formData.nationality}</span>
                                        </div>
                                    </div>

                                    {computeAge(formData.birthday) !== null && computeAge(formData.birthday) < 18 && (
                                        <div className="border border-amber-400/50 bg-amber-400/10 rounded-xl p-4 space-y-3">
                                            <p className="text-amber-200 text-sm font-medium">Du bist unter 18. Deine Erziehungsberechtigten müssen zustimmen.</p>
                                            <p className="text-white/60 text-xs">Bitte Name und Kontakt angeben und die unterschriebene Erklärung (PDF oder Foto) hochladen. Ohne diese Angaben kann die Bewerbung nicht abgeschickt werden.</p>
                                            <Input value={guardian.name} onChange={(e) => setGuardian(g => ({ ...g, name: e.target.value }))} placeholder="Name der/des Erziehungsberechtigten *" className="bg-white/10 border-white/20 text-white" />
                                            <Input value={guardian.contact} onChange={(e) => setGuardian(g => ({ ...g, contact: e.target.value }))} placeholder="Telefon oder E-Mail der/des Erziehungsberechtigten *" className="bg-white/10 border-white/20 text-white" />
                                            <div className="flex items-center gap-3">
                                                <input type="file" accept="application/pdf,image/*" onChange={uploadGuardianDoc} className="text-white/70 text-xs" disabled={guardian.uploading} />
                                                {guardian.uploading && <span className="text-white/60 text-xs">lädt …</span>}
                                                {guardian.doc_url && <span className="text-green-400 text-xs">✓ hochgeladen</span>}
                                            </div>
                                        </div>
                                    )}

                                    <div className="border-t border-white/20 pt-4 mt-4 space-y-3">
                                        <p className="text-white/60 text-xs">Bitte jede Erklärung einzeln lesen und bestätigen. Alle vier sind Pflicht.</p>
                                        {consentDocs.map(d => (
                                            <label key={d.key} className="flex items-start gap-3 cursor-pointer p-2 rounded-lg hover:bg-white/5">
                                                <Checkbox
                                                    checked={!!consents[d.key]}
                                                    onCheckedChange={(c) => setConsents(s => ({ ...s, [d.key]: !!c }))}
                                                    className="mt-1"
                                                    data-testid={`consent-${d.key}`}
                                                />
                                                <span className="text-white text-sm">
                                                    Ich stimme zu:{' '}
                                                    <a href={d.url} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">{d.title}</a>
                                                    <span className="text-white/40 text-xs"> (Fassung {d.version}) *</span>
                                                    {d.short && <span className="block text-white/50 text-xs mt-0.5">{d.short}</span>}
                                                </span>
                                            </label>
                                        ))}
                                        {consentDocs.length === 0 && (
                                            <p className="text-amber-300 text-xs">Erklärungen werden geladen … Falls nichts erscheint, bitte Seite neu laden.</p>
                                        )}
                                    </div>

                                    {/* Turnstile CAPTCHA */}
                                    {TURNSTILE_SITE_KEY && (
                                        <div className="flex justify-center pt-4">
                                            <Turnstile
                                                siteKey={TURNSTILE_SITE_KEY}
                                                onSuccess={(token) => setTurnstileToken(token)}
                                                onError={() => setTurnstileToken(null)}
                                                onExpire={() => setTurnstileToken(null)}
                                                options={{ theme: 'dark' }}
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>
                </div>

                {/* Navigation */}
                <div className="flex gap-4 mt-6">
                    {currentSlide > 0 && (
                        <Button
                            variant="outline"
                            onClick={goPrev}
                            className="flex-1 bg-white/10 border-white/20 text-white hover:bg-white/20"
                        >
                            <ChevronLeft className="w-4 h-4 mr-2" /> Zurück
                        </Button>
                    )}
                    
                    {currentSlide < totalSlides - 1 ? (
                        <Button
                            onClick={goNext}
                            disabled={!canProceed()}
                            className="flex-1"
                            style={{ background: canProceed() ? 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' : undefined }}
                        >
                            Weiter <ChevronRight className="w-4 h-4 ml-2" />
                        </Button>
                    ) : (
                        <Button
                            onClick={handleSubmit}
                            disabled={!canProceed() || isSubmitting}
                            className="flex-1"
                            style={{ background: 'linear-gradient(135deg, hsl(199, 100%, 50%) 0%, hsl(199, 100%, 40%) 100%)' }}
                        >
                            {isSubmitting || isUploading ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    {isUploading ? 'Bild wird hochgeladen...' : 'Wird gesendet...'}
                                </>
                            ) : (
                                <>Bewerbung absenden</>
                            )}
                        </Button>
                    )}
                </div>

                {/* Consent Popup */}
                <AnimatePresence>
                    {showConsentPopup && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50"
                            onClick={() => setShowConsentPopup(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.9 }}
                                className="bg-slate-800 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <h3 className="text-xl font-bold text-white mb-4">
                                    Einverständniserklärung Bildrechte
                                </h3>
                                <div className="text-white/70 text-sm space-y-4 mb-6">
                                    <p>
                                        Hiermit erteile ich meine ausdrückliche Zustimmung zur Herstellung von 
                                        Bild- und Videoaufnahmen meiner Person, durch die The Guardians of Social Media GmbH.
                                    </p>
                                    <p>
                                        Des Weiteren erteile ich hiermit meine Einverständnis, dass die Bild- und 
                                        Videoaufnahmen samt den in den Dateieigenschaften befindlichen weiteren 
                                        personenbezogenen Daten (Name, Vorname, etc.) von der The Guardians of 
                                        Social Media GmbH und/oder von beauftragten Dritten zeitlich, inhaltlich 
                                        und räumlich unbeschränkt – insbesondere aber nicht nur zu Werbezwecken – 
                                        in Medien jeglicher Form veröffentlicht, vervielfältigt, verbreitet und 
                                        verwertet sowie digitalisiert, elektronisch gespeichert und verändert werden dürfen.
                                    </p>
                                </div>
                                <div className="flex gap-3">
                                    {consentPdfUrl && (
                                        <a
                                            href={consentPdfUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-1"
                                        >
                                            <Button variant="outline" className="w-full">
                                                PDF herunterladen
                                            </Button>
                                        </a>
                                    )}
                                    <Button onClick={() => setShowConsentPopup(false)} className="flex-1">
                                        Schließen
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
                </div>
            </div>
            
            {/* Footer */}
            <footer className="py-6 text-center text-white/40 text-sm">
                <p>© {new Date().getFullYear()} The Guardians of Social Media GmbH. Alle Rechte vorbehalten.</p>
            </footer>
        </div>
    );
};

export default SocialFacesApplicationPage;
